import { useEffect, useState } from 'react';
import { Button, Message, Modal } from 'semantic-ui-react';
import { isAssociateManagerOrAbove } from '../auth';
import { LoadingWrapper, ReactTable } from '../common';
import { getMoveRequests, cancelMoveRequest } from './assetMoveService';
import { MoveAssetModal } from './MoveAssetModal';
import { formatDateForDisplay } from '../../utils/formatDate';

const STATUS_LABEL = { pending: 'Pending', complete: 'Complete', cancelled: 'Cancelled' };

const STATUS_STYLE = {
    pending: { background: '#FBBD08', color: '#000', padding: '2px 8px', borderRadius: 4, display: 'inline-block' },
    complete: { background: '#21BA45', color: '#fff', padding: '2px 8px', borderRadius: 4, display: 'inline-block' },
    cancelled: { background: '#767676', color: '#fff', padding: '2px 8px', borderRadius: 4, display: 'inline-block' },
};

export const AssetMoveHistory = ({ iccName, currentTeamId, onAssetRefresh }) => {
    const [moveRequests, setMoveRequests] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [successMsg, setSuccessMsg] = useState(null);
    const [error, setError] = useState(null);
    const [editTarget, setEditTarget] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [notesTarget, setNotesTarget] = useState(null);

    async function fetchMoveRequests() {
        const data = await getMoveRequests(iccName);
        data.sort((a, b) => {
            const byMoveDate = (b.moveDate || '').localeCompare(a.moveDate || '');
            if (byMoveDate !== 0) return byMoveDate;
            return (b.creationTime || '').localeCompare(a.creationTime || '');
        });
        setMoveRequests(data);
    }

    async function loadData() {
        if (!iccName) {
            setLoaded(true);
            return;
        }
        setLoaded(false);
        try {
            await fetchMoveRequests();
        } catch (e) {
            setError('Failed to load move history.');
        } finally {
            setLoaded(true);
        }
    }

    useEffect(() => {
        loadData();
    }, [iccName]);

    async function handleCancel(moveId) {
        if (!window.confirm('Are you sure you want to cancel this move request?')) return;
        setError(null);
        try {
            await cancelMoveRequest(moveId);
            setSuccessMsg('Move request cancelled.');
            await fetchMoveRequests();
        } catch {
            setError('Failed to cancel move request.');
        }
    }

    async function handleMoveSuccess(result) {
        setShowCreateModal(false);
        setEditTarget(null);
        const isImmediate = result.status === 'complete';
        setSuccessMsg(
            isImmediate
                ? `Asset moved to ${result.targetTeamName || 'new team'}.`
                : 'Move scheduled successfully.'
        );
        await loadData();
        onAssetRefresh?.();
    }

    const hasPending = moveRequests.some(r => r.status === 'pending');

    const columnInfo = [
        {
            Header: 'Move Date',
            accessor: 'moveDate',
            Cell: ({ row }) => formatDateForDisplay(row.original.moveDate),
        },
        {
            Header: 'From Team',
            accessor: 'previousTeamName',
            Cell: ({ row }) => row.original.previousTeamName || 'Unassigned',
        },
        {
            Header: 'To Team',
            accessor: 'targetTeamName',
            Cell: ({ row }) => row.original.targetTeamName || '-',
        },
        {
            Header: 'Status',
            accessor: 'status',
            Cell: ({ row }) => {
                const s = row.original.status;
                return <span style={STATUS_STYLE[s] || {}}>{STATUS_LABEL[s] || s}</span>;
            },
        },
        {
            Header: 'Requested By',
            accessor: 'requestedByName',
        },
        {
            Header: 'Submitted On',
            accessor: 'creationTime',
            Cell: ({ row }) => formatDateForDisplay(row.original.creationTime),
        },
        {
            Header: 'Actions',
            id: 'actions',
            Cell: ({ row }) => {
                const isPendingManager = row.original.status === 'pending' && isAssociateManagerOrAbove();
                const hasNotes = !!row.original.notes;
                if (!isPendingManager && !hasNotes) return null;
                return (
                    <div style={{ display: 'flex', gap: 4 }}>
                        {hasNotes && (
                            <Button size="tiny" basic onClick={() => setNotesTarget(row.original.notes)}>View Notes</Button>
                        )}
                        {isPendingManager && (
                            <>
                                <Button size="tiny" onClick={() => setEditTarget(row.original)}>Edit</Button>
                                <Button size="tiny" color="red" onClick={() => handleCancel(row.original.id)}>Cancel</Button>
                            </>
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <div>
            {successMsg && (
                <Message positive onDismiss={() => setSuccessMsg(null)} content={successMsg} />
            )}
            {error && (
                <Message negative onDismiss={() => setError(null)} content={error} />
            )}
            {isAssociateManagerOrAbove() && (
                hasPending
                    ? <Message info style={{ marginBottom: '1rem' }} content="A move request is already pending for this asset. Please wait for it to be resolved or cancel it before scheduling a new one." />
                    : <Button primary style={{ marginBottom: '1rem' }} onClick={() => setShowCreateModal(true)}>Schedule Move</Button>
            )}
            <LoadingWrapper loading={!loaded} text="Loading move history">
                {moveRequests.length === 0
                    ? <p>No move history for this asset.</p>
                    : (
                        <ReactTable
                            className="channel-table"
                            data={moveRequests}
                            columns={columnInfo}
                        />
                    )
                }
            </LoadingWrapper>
            {showCreateModal && (
                <MoveAssetModal
                    iccName={iccName}
                    currentTeamId={currentTeamId}
                    onSuccess={handleMoveSuccess}
                    onClose={() => setShowCreateModal(false)}
                />
            )}
            {editTarget && (
                <MoveAssetModal
                    iccName={iccName}
                    currentTeamId={currentTeamId}
                    existingId={editTarget.id}
                    initialValues={editTarget}
                    onSuccess={handleMoveSuccess}
                    onClose={() => setEditTarget(null)}
                />
            )}
            <Modal open={!!notesTarget} onClose={() => setNotesTarget(null)} size="small">
                <Modal.Header>Notes</Modal.Header>
                <Modal.Content>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{notesTarget}</p>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setNotesTarget(null)}>Close</Button>
                </Modal.Actions>
            </Modal>
        </div>
    );
};
